import { clsx } from 'clsx';
import { uniqueId } from 'lodash-es';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Icon from './Icon';
import { ListBoxOption } from './Dropdown';

interface ListboxProps {
  optionList: ListBoxOption[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedOption?: ListBoxOption;
  setSelectedOption: (option: ListBoxOption) => void;
  inputSize?: 'small' | 'medium' | 'large';
  listClassname?: string;
  buttonClassName?: string;
  continerClassName?: string;
  label: string;
  showLabel?: boolean;
  showIconOnSelectedItem?: boolean;
  showStickyLabel?: boolean;
}

export const Listbox = ({
  optionList,
  isOpen,
  setIsOpen,
  selectedOption,
  setSelectedOption,
  inputSize = 'medium',
  listClassname,
  buttonClassName,
  continerClassName,
  showIconOnSelectedItem = false,
  showLabel,
  label,
  showStickyLabel = false,
}: ListboxProps) => {
  const [activeOptionIndex, setActiveOption] = useState<number | null>(
    null
  );
  const [typeAheadString, setTypeAheadString] = useState<string>('');
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const typeAheadTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const buttonRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const WAITING_TIME_FOR_ANIMATION = 200;
  const uuid = uniqueId();

  const closeOnEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    },
    [isOpen]
  );

  const closePanel = useCallback((): void => {
    const panel = panelRef.current;
    if (panel) {
      panel.classList.remove('opacity-100');
      panel.classList.add('opacity-0');
      panel.classList.add('pointer-events-none');
    }
    setTimeout(() => {
      setIsOpen(false);
    }, WAITING_TIME_FOR_ANIMATION);
    if (selectedOption) {
      setActiveOption(
        optionList.findIndex(
          (option) => option.value === selectedOption.value
        )
      );
    }
  }, [setIsOpen, panelRef]);

  const handleDocumentClick = useCallback(
    (event: MouseEvent): void => {
      if (
        wrapperRef?.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closePanel();
      }
    },
    [closePanel]
  );

  const handleTriggerButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    setIsOpen(!isOpen);
    keepActiveElementInViewport();
  };
  const handleOnMouseOver = (index: number) => {
    if (index === activeOptionIndex || isOptionDisabled(index)) return;
    setActiveOption(index);
  };

  const handleOptionClick = (index: number) => {
    if (isOptionDisabled(index)) return;
    setSelectedOption(optionList[index]);
    closePanel();
  };

  const keepActiveElementInViewport = useCallback(() => {
    const list = listRef.current;
    const activeElement = list?.querySelector('[data-active=true]');

    if (!activeElement || !list) return;

    const panelBounds = list.getBoundingClientRect();
    const activeBounds = activeElement.getBoundingClientRect();

    if (activeBounds.top < panelBounds.top) {
      list.scrollTop -= panelBounds.top - activeBounds.top;
    } else if (activeBounds.bottom > panelBounds.bottom) {
      list.scrollTop += activeBounds.bottom - panelBounds.bottom;
    }
  }, []);

  const firstNoneDisabledIndex = useMemo(() => {
    return optionList.findIndex((option) => !option.disabled);
  }, [optionList]);

  const isOptionDisabled = (index: number) => optionList[index].disabled;

  useEffect(() => {
    const panel = panelRef.current;
    const list = listRef.current;
    const addEventListeners = () => {
      document.addEventListener('click', handleDocumentClick);
      document.addEventListener('keydown', closeOnEscape);
    };
    const removeEventListeners = () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
    if (!selectedOption && !label) {
      setSelectedOption(optionList[firstNoneDisabledIndex]);
      setActiveOption(firstNoneDisabledIndex);
    }
    if (!hasBeenOpened && !isOpen) return;
    if (isOpen) {
      setHasBeenOpened(true);
      addEventListeners();
      if (!selectedOption) {
        setActiveOption(firstNoneDisabledIndex);
      }
      if (panel && list) {
        list.focus();
        panel.classList.remove('pointer-events-none');
        setTimeout(() => {
          panel.classList.remove('opacity-0');
          panel.classList.add('opacity-100');
        }, 100);
      }
    } else {
      removeEventListeners();
      buttonRef.current?.focus();
      if (panel) {
        panel.classList.remove('opacity-100');
        panel.classList.add('opacity-0');
        panel.classList.add('pointer-events-none');
      }
    }
    return () => {
      removeEventListeners();
    };
  }, [closeOnEscape, handleDocumentClick, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const containerElement = panelRef.current;
    if (!containerElement) return;

    const handleEscapeKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    };

    const handleTypeAhead = (event: KeyboardEvent) => {
      const key = event.key;
      event.preventDefault();

      if (key.length === 1) {
        if (typeAheadTimeout.current) {
          clearTimeout(typeAheadTimeout.current);
        }
        setTypeAheadString((prev) => {
          const newString = prev + key.toLowerCase();
          const index = optionList.findIndex((option) =>
            option.label.toLowerCase().startsWith(newString)
          );
          if (index !== -1 && !isOptionDisabled(index)) {
            setActiveOption(index);
          }
          return newString;
        });
        typeAheadTimeout.current = setTimeout(() => {
          setTypeAheadString('');
        }, 500);
      }
    };
    const handleArrowDown = (e: KeyboardEvent) => {
      e.preventDefault();
      setActiveOption((prevIndex) => {
        let newIndex = prevIndex === null ? 0 : prevIndex + 1;
        while (newIndex < optionList.length) {
          if (!optionList[newIndex].disabled) {
            return newIndex;
          }
          newIndex++;
        }
        return prevIndex;
      });
    };

    const handleArrowUp = (e: KeyboardEvent) => {
      e.preventDefault();
      setActiveOption((prevIndex) => {
        let newIndex =
          prevIndex === null ? optionList.length - 1 : prevIndex - 1;

        while (newIndex >= 0) {
          if (!optionList[newIndex].disabled) {
            return newIndex;
          }
          newIndex--;
        }
        return prevIndex;
      });
    };

    const handleEndKeyPress = (event: KeyboardEvent) => {
      event.preventDefault();
      setActiveOption((prevIndex) => {
        for (let index = optionList.length - 1; index >= 0; index--) {
          if (!isOptionDisabled(index)) {
            return index;
          }
        }
        return prevIndex;
      });
    };

    const handleHomeKeyPress = (event: KeyboardEvent) => {
      event.preventDefault();
      setActiveOption((prevIndex) => {
        for (let index = 0; index < optionList.length; index++) {
          if (!isOptionDisabled(index)) {
            return index;
          }
        }
        return prevIndex;
      });
    };

    const handleArrowKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        handleArrowDown(event);
      } else if (event.key === 'ArrowUp') {
        handleArrowUp(event);
      } else if (event.key === 'Home') {
        handleHomeKeyPress(event);
      } else if (event.key === 'End') {
        handleEndKeyPress(event);
      }
    };
    containerElement?.addEventListener('keydown', handleEscapeKeyPress);
    containerElement?.addEventListener('keydown', handleArrowKeyPress);
    containerElement?.addEventListener('keydown', handleTypeAhead);
    return () => {
      containerElement?.removeEventListener(
        'keydown',
        handleEscapeKeyPress
      );
      containerElement?.removeEventListener(
        'keydown',
        handleArrowKeyPress
      );
      containerElement?.removeEventListener('keydown', handleTypeAhead);
    };
  }, [activeOptionIndex, closePanel, optionList, isOpen]);

  const getSizeClass = (size: 'small' | 'medium' | 'large' | 'xlarge') => {
    switch (size) {
      case 'large':
        return 'py-3 px-4';
      default:
        return 'px-3 py-2';
    }
  };

  return (
    <div
      ref={wrapperRef as RefObject<HTMLDivElement>}
      className={clsx(
        'listbox relative hidden w-full text-gray-900 lg:block ',
        continerClassName
      )}
    >
      {showLabel && (
        <label
          htmlFor={'button-' + uuid}
          className="mb-2 flex text-base font-bold text-gray-900"
        >
          {label}
        </label>
      )}
      {showStickyLabel && (
        <label
          htmlFor={'button-' + uuid}
          className="absolute -top-2 left-[14px] bg-white px-1 text-sm text-gray-700"
        >
          {label}
        </label>
      )}
      <button
        ref={buttonRef as RefObject<HTMLButtonElement>}
        className={clsx(
          'flex w-full cursor-pointer appearance-none items-center rounded border border-solid border-gray-300 bg-white px-3 py-2 text-lg',
          getSizeClass(inputSize),
          buttonClassName
        )}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls={uuid}
        aria-label={!showLabel ? label : undefined}
        aria-haspopup="listbox"
        onClick={(e) => handleTriggerButtonClick(e)}
        type="button"
        id={'button-' + uuid}
      >
        <span className="flex-1 text-start">
          {selectedOption ? selectedOption.label : label}
        </span>
        <Icon
          name="chevron-down"
          className={clsx(
            'transition duration-300',
            isOpen === true ? 'rotate-180' : ''
          )}
        />
      </button>
      <div
        className="pointer-events-none absolute top-full z-50 mt-1 w-full overflow-hidden rounded-lg border border-solid border-gray-300 bg-white opacity-0 transition-opacity !duration-[200ms]"
        ref={panelRef as RefObject<HTMLDivElement>}
        aria-hidden={isOpen ? 'false' : 'true'}
      >
        <ul
          className={clsx(
            'z-10 max-h-80 w-full overflow-y-scroll shadow-[0px_13px_37px_0px_rgba(0,0,0,0.21)] scrollbar-none focus:outline-none',
            listClassname
          )}
          ref={listRef as RefObject<HTMLUListElement>}
          role="listbox"
          aria-labelledby={label ?? 'button-' + uuid}
          aria-orientation="vertical"
          aria-multiselectable={'false'}
          aria-activedescendant={
            activeOptionIndex !== null
              ? optionList[activeOptionIndex]?.value
              : undefined
          }
          id={uuid}
          tabIndex={isOpen ? 0 : -1}
          onKeyDown={(event) => {
            if (activeOptionIndex !== null && event.key === 'Enter') {
              setSelectedOption(optionList[activeOptionIndex!]);
              closePanel();
            } else if (
              event.key === ' ' &&
              typeAheadString.trim().length === 0
            ) {
              setSelectedOption(optionList[activeOptionIndex!]);
              closePanel();
            } else {
              keepActiveElementInViewport();
            }
          }}
        >
          {optionList.map((option, idx) => (
            <li
              className="w-full cursor-pointer border-b border-solid border-gray-300 px-3 py-2.5 text-md aria-disabled:cursor-not-allowed aria-selected:font-bold data-[active=true]:bg-gray-100 hover:bg-gray-100 [&:last-child]:border-b-0"
              role="option"
              id={option.value}
              aria-selected={option.selected}
              key={option.value ? option.value + idx : 'value' + idx}
              data-active={activeOptionIndex === idx}
              onMouseOver={() => handleOnMouseOver(idx)}
              aria-disabled={option.disabled}
              onClick={() => {
                handleOptionClick(idx);
              }}
            >
              {showIconOnSelectedItem && (
                <span
                  aria-hidden="true"
                  className="mr-2 flex w-6 items-center justify-center text-gray-900"
                >
                  {selectedOption?.value === option.value ? (
                    <Icon name="check-circle" className="w-4" />
                  ) : (
                    ''
                  )}
                </span>
              )}
              {option.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Listbox;
